import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { Category } from "../types/category";
import {
    categorySearchQuerySchema,
    categorySearchResponseSchema,
    createCategoryBodySchema,
    createCategoryWithGlMappingsBodySchema,
    updateCategoryBodySchema,
    updateCategoryGlMappingsBodySchema,
} from "../schemas/category.schema";

export interface CategoryStat {
    id: string;
    parentCategoryId?: string;
    label: string;
    total: number;
    count: number;
    groupCount: number;
}

export type CategoryGlMappingResponseState =
    | "default"
    | "uniform"
    | "divergent"
    | "advanced";

export interface CategoryGlMapping {
    categoryId: string;
    state: CategoryGlMappingResponseState;
    inAccountName?: string;
    outAccountName?: string;
}

const c = initContract();

export const categoryContract = c.router(
    {
        getCategories: {
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "GET",
            responses: {
                200: c.type<{ categories: Category[] }>(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
        searchCategories: {
            path: "/:workspaceId/search",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "GET",
            query: categorySearchQuerySchema,
            responses: {
                200: categorySearchResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        getGlobalCategories: {
            path: "/global",
            method: "GET",
            responses: {
                200: c.type<{ categories: Category[] }>(),
            },
        },
        getCategoryStatistics: {
            path: "/:workspaceId/statistics",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "GET",
            responses: {
                200: c.type<CategoryStat[]>(),
            },
            query: z.object({
                entityIds: z.array(z.coerce.number()).optional(),
                start: z.coerce.date().optional(),
                end: z.coerce.date().optional(),
            }),
        },
        getCategoryGlMappings: {
            path: "/:workspaceId/gl-mappings",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "GET",
            responses: {
                200: c.type<CategoryGlMapping[]>(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createCategory: {
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "POST",
            body: createCategoryBodySchema,
            responses: {
                201: c.type<Category>(),
                400: c.type<{ message: string }>(),
            },
        },
        createCategoryWithGlMappings: {
            path: "/:workspaceId/with-gl-mappings",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "POST",
            body: createCategoryWithGlMappingsBodySchema,
            responses: {
                201: c.type<Category>(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateCategory: {
            path: "/:workspaceId/:categoryId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                categoryId: z.string().uuid(),
            }),
            method: "PATCH",
            body: updateCategoryBodySchema,
            responses: {
                201: c.type<Category>(),
                400: c.type<{ message: string }>(),
            },
        },
        updateCategoryGlMappings: {
            path: "/:workspaceId/:categoryId/gl-mappings",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                categoryId: z.string().uuid(),
            }),
            method: "PATCH",
            body: updateCategoryGlMappingsBodySchema,
            responses: {
                200: c.type<Category>(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteCategory: {
            path: "/:workspaceId/:categoryId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                categoryId: z.string().uuid(),
            }),
            method: "DELETE",
            responses: {
                200: c.noBody(),
            },
        },
    },
    {
        pathPrefix: "/category",
    },
);
